// QuickStudyPdfPicker — modal exibido depois que o aluno escolhe um PDF.
// Mostra thumbnails de cada página e deixa selecionar até MAX_PAGES (default 5)
// pra rasterizar como imagens. Auto-confirma se o PDF tem só 1 página.

import { useEffect, useState } from "preact/hooks";
import { Check, FileText, Loader2, X } from "lucide-react";
import {
  rasterizePdfPages,
  renderPdfThumbnails,
  type PdfPagePreview
} from "./quickStudyPdf";
import type { QuickStudyImageInput } from "./api";

interface Props {
  file: File;
  /** Quantas páginas o aluno pode escolher no máximo. */
  maxPages?: number;
  onCancel: () => void;
  onConfirm: (images: QuickStudyImageInput[]) => void;
}

export function QuickStudyPdfPicker({
  file,
  maxPages = 5,
  onCancel,
  onConfirm
}: Props) {
  const [thumbs, setThumbs] = useState<PdfPagePreview[] | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [selected, setSelected] = useState<Set<number>>(new Set([1]));
  const [loading, setLoading] = useState(true);
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const r = await renderPdfThumbnails(file, { maxPages: 50 });
        if (cancelled) return;
        setThumbs(r.thumbs);
        setTotalPages(r.totalPages);
        // Se for 1 página, gera direto (sem fazer aluno clicar Confirmar).
        if (r.totalPages === 1) {
          await doConfirm([1]);
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof Error
              ? e.message
              : "Não consegui ler o PDF. Tente outro arquivo."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [file]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  function togglePage(pageNum: number) {
    const next = new Set(selected);
    if (next.has(pageNum)) {
      next.delete(pageNum);
    } else if (next.size >= maxPages) {
      setError(`Limite de ${maxPages} páginas por dúvida.`);
      return;
    } else {
      next.add(pageNum);
    }
    setError(null);
    setSelected(next);
  }

  async function doConfirm(pages?: number[]) {
    const list = pages ?? Array.from(selected).sort((a, b) => a - b);
    if (list.length === 0) {
      setError("Selecione pelo menos uma página.");
      return;
    }
    setRendering(true);
    setError(null);
    try {
      const imgs = await rasterizePdfPages(file, list, { maxPages });
      onConfirm(imgs);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Falha ao processar páginas do PDF."
      );
    } finally {
      setRendering(false);
    }
  }

  const selectedCount = selected.size;

  return (
    <div class="fixed inset-0 z-[110] flex flex-col bg-black/90 backdrop-blur-sm">
      <header class="flex shrink-0 items-center justify-between gap-2 border-b border-white/10 px-3 py-2">
        <div class="flex min-w-0 items-center gap-2">
          <FileText className="size-4 shrink-0 text-violet-400" />
          <div class="min-w-0">
            <div class="truncate text-sm font-bold text-white">{file.name}</div>
            {totalPages > 0 && (
              <div class="text-[11px] text-white/60">
                {totalPages} página{totalPages === 1 ? "" : "s"} · escolha até{" "}
                {maxPages}
              </div>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Fechar"
          title="Cancelar"
          class="rounded-md p-1.5 text-white hover:bg-white/10"
        >
          <X className="size-5" />
        </button>
      </header>

      <div class="flex-1 overflow-y-auto p-3">
        {loading && (
          <div class="flex h-64 items-center justify-center gap-2 text-sm text-white">
            <Loader2 className="size-4 animate-spin" /> Lendo páginas…
          </div>
        )}

        {!loading && thumbs && thumbs.length > 0 && (
          <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {thumbs.map((t) => {
              const isSel = selected.has(t.pageNumber);
              return (
                <button
                  type="button"
                  key={t.pageNumber}
                  onClick={() => togglePage(t.pageNumber)}
                  class={`relative overflow-hidden rounded-md border-2 transition-all ${
                    isSel
                      ? "border-violet-500 ring-2 ring-violet-500/50"
                      : "border-white/20 hover:border-white/40"
                  }`}
                >
                  <img
                    src={t.thumbDataUrl}
                    alt={`Página ${t.pageNumber}`}
                    class="w-full bg-white"
                  />
                  <div class="absolute left-1 top-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-bold text-white">
                    {t.pageNumber}
                  </div>
                  {isSel && (
                    <div class="absolute right-1 top-1 rounded-full bg-violet-600 p-1 text-white">
                      <Check className="size-3" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {error && (
          <div class="mt-3 rounded-md bg-destructive/20 px-3 py-2 text-sm text-destructive-foreground">
            {error}
          </div>
        )}
      </div>

      <footer class="flex shrink-0 items-center justify-between gap-2 border-t border-white/10 bg-black/40 px-4 py-3">
        <div class="text-xs text-white/70">
          {selectedCount > 0 && (
            <span>
              <span class="font-bold text-white">{selectedCount}</span>{" "}
              página{selectedCount === 1 ? "" : "s"} selecionada
              {selectedCount === 1 ? "" : "s"}
            </span>
          )}
        </div>
        <div class="flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            class="rounded-md border border-white/20 px-3 py-2 text-sm font-semibold text-white hover:bg-white/10"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={rendering || selectedCount === 0 || loading}
            onClick={() => doConfirm()}
            class="inline-flex items-center gap-2 rounded-md bg-violet-600 px-4 py-2 text-sm font-bold text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {rendering ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Check className="size-4" />
            )}
            Usar páginas
          </button>
        </div>
      </footer>
    </div>
  );
}
